import {useEffect, useMemo, useRef, useState} from "react";
import useCreateTicket from "./useCreateTicket";
import {
  OUTCOME_LIMIT,
  applyLimitIssues,
  applyPurchaseOutcome,
  applySuggestion,
  beginIntent,
  buildSubmissionPayload,
  canConfirm,
  clearRecovery,
  defaultIdGenerator,
  keepAttemptForRetry,
  newAttemptForRevision,
  payableRows,
  payloadSignature,
  payableTotalBs,
  removeRowById,
  selectionKey,
} from "../utils/lotteryTicketRecovery";

// Shared limit-recovery controller for the standard Randrush lottery
// ticket flow. It wraps `useCreateTicket` and owns the intent/attempt correlation,
// the reviewed payable draft and the recovery state machine (structured limit
// issues, uncertain-retry protection, partial-acceptance residual, confirmation
// gating) using the pure helpers in `lotteryTicketRecovery.js`.
//
// Every in-scope board (DefaultLottery figures, TripleBoard, TerminalBoard) uses
// this hook so the recovery behaviour is identical: over-limit rows offer an
// explicit `Usar Bs X` suggestion applied to a NEW payable amount while keeping the
// original requested demand, unavailable/below-minimum rows move to a bottom
// section, confirm is disabled until resolved or while a request is pending, and a
// retry after an uncertain outcome reuses the SAME attempt id (the backend
// idempotency guard then prevents a second sale).
//
// opts.onSettled: called after a fully accepted purchase so the caller can clear
// its own draft and close the modal.
export default function useLotteryRecovery(opts) {
  const onSettledRef = useRef(opts?.onSettled);
  onSettledRef.current = opts?.onSettled;

  const {postCreateTicket, isPending, isSuccess, error, data, reset, outcomeRef} = useCreateTicket({
    handleStructuredLimits: true,
  });

  const [correlation, setCorrelation] = useState(null);
  const [recovery, setRecovery] = useState(null);

  const rowIdMapRef = useRef(new Map());
  const draftRef = useRef([]);
  const lastSigRef = useRef(null);

  const resolveRowId = meta => {
    const key = selectionKey(meta);
    let id = rowIdMapRef.current.get(key);
    if (!id) {
      // The API persists client_bet_id as a UUID. Keep this ID stable for the
      // selection while allowing the backend to validate and deduplicate it.
      id = defaultIdGenerator();
      rowIdMapRef.current.set(key, id);
    }
    return id;
  };

  const resetAll = () => {
    setCorrelation(null);
    setRecovery(null);
    rowIdMapRef.current.clear();
    draftRef.current = [];
    lastSigRef.current = null;
  };

  // Submit the caller-provided authoritative draft (during review the caller
  // passes the reviewed rows). The attempt id follows a payload-signature rule: an
  // unchanged draft after an UNCERTAIN failure (network / pre-sale 503) replays the
  // SAME attempt so the backend idempotency guard blocks a double sale; any player
  // revision or changed draft after a definitive rejection mints a NEW attempt under
  // the SAME intent.
  const submit = (rows, submitCtx) => {
    if (isPending) return;
    const source = rows || [];
    if (payableRows(source).length === 0) return;
    if (recovery?.blockResubmit) return;
    const sig = payloadSignature(source);
    let corr = correlation;
    if (!corr) {
      corr = beginIntent();
    } else if (recovery?.reuseAttempt && sig === lastSigRef.current) {
      corr = keepAttemptForRetry(corr);
    } else if (recovery) {
      corr = newAttemptForRevision(corr);
    } else {
      corr = beginIntent();
    }
    setCorrelation(corr);
    lastSigRef.current = sig;
    draftRef.current = source;
    const payload = buildSubmissionPayload(source, {
      ...submitCtx,
      intentId: corr.intentId,
      attemptId: corr.attemptId,
    });
    reset(); // clear stale success/error before the next submission
    postCreateTicket(payload);
  };

  const handleUseSuggestion = rowId => setRecovery(r => (r ? {...r, rows: applySuggestion(r.rows, rowId)} : r));
  const handleRemoveRow = rowId => setRecovery(r => (r ? {...r, rows: removeRowById(r.rows, rowId)} : r));
  const handleDiscard = () => resetAll();

  // Structured / uncertain error handling: keep the draft, never wipe it.
  useEffect(() => {
    if (!error) return;
    const outcome = outcomeRef.current || {kind: "unknown"};
    const source = recovery?.rows || draftRef.current || [];
    if (outcome.kind === OUTCOME_LIMIT && Array.isArray(outcome.issues)) {
      const applied = applyLimitIssues(source, outcome.issues);
      setRecovery({
        phase: "review",
        rows: applied.rows,
        mappingUncertain: applied.mappingUncertain,
        outcome,
        message: applied.mappingUncertain
          ? "Algunos límites no se pudieron asociar con seguridad a una jugada concreta."
          : "Ajusta o retira las jugadas marcadas antes de confirmar.",
      });
      return;
    }
    if (outcome.kind === "network" || outcome.kind === "store_unavailable") {
      setRecovery({phase: "review", rows: clearRecovery(source), uncertain: true, reuseAttempt: true, outcome, message: outcome.message});
      return;
    }
    if (outcome.blockResubmit || outcome.uncertain || ["unresolved", "server", "unknown"].includes(outcome.kind)) {
      setRecovery({
        phase: "review",
        rows: clearRecovery(source),
        uncertain: true,
        blockResubmit: true,
        outcome,
        message: outcome.message || "No pudimos confirmar el estado de tu apuesta. Revisa tus jugadas antes de reintentar.",
      });
      return;
    }
    setRecovery({phase: "review", rows: clearRecovery(source), outcome, message: outcome.message || "No se pudo procesar tu apuesta."});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  // Success handling: only a confidently-sold purchase clears the draft.
  useEffect(() => {
    if (!isSuccess) return;
    const source = recovery?.rows || draftRef.current || [];
    const result = applyPurchaseOutcome(source, data);
    if (result.uncertain) {
      setRecovery({
        phase: "review",
        rows: result.rows,
        uncertain: true,
        blockResubmit: true,
        message: "No pudimos confirmar el estado exacto de tu compra. Revisa tus jugadas antes de volver a apostar.",
      });
      return;
    }
    if (payableRows(result.rows).length > 0) {
      setRecovery({phase: "review", rows: result.rows, partial: true, message: "Parte de tu apuesta fue aceptada. Puedes confirmar solo la parte pendiente."});
      return;
    }
    resetAll();
    onSettledRef.current?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  const reviewProps = useMemo(() => {
    if (recovery?.phase !== "review") return {recovery: null, reviewCanConfirm: false, reviewTotal: 0};
    return {
      recovery,
      reviewCanConfirm: canConfirm(recovery.rows, {isPending, blockResubmit: recovery.blockResubmit, locked: isPending}),
      reviewTotal: payableTotalBs(recovery.rows),
    };
  }, [recovery, isPending]);

  return {
    // mutation state passthrough (backwards compatible names)
    isPending,
    isSuccess,
    isError: !!error,
    error,
    data,
    // recovery
    resolveRowId,
    submit,
    resetAll,
    handleUseSuggestion,
    handleRemoveRow,
    handleDiscard,
    ...reviewProps,
  };
}
